import { useState } from 'react';
import { useList, useTranslate } from '@refinedev/core';
import { Button, Card, Empty, Flex, Timeline, Typography, theme } from 'antd';
import type { SessionHourlyOut } from '@/api/generated/schemas';
import { formatDateTime, formatSecondsVerbose } from '@/utils/format';

const PAGE = 10;

/** Hourly continuous-aggregate buckets for one session, newest first. */
export const SessionHourlyCard = ({ sessionId }: { sessionId: number }) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [limit, setLimit] = useState(PAGE);

  const { result, query } = useList<SessionHourlyOut>({
    resource: 'progress.session_hourly',
    filters: [{ field: 'session_id', operator: 'eq', value: sessionId }],
    sorters: [{ field: 'bucket', order: 'desc' }],
    pagination: { pageSize: limit, mode: 'server' },
  });

  const buckets = result?.data ?? [];

  return (
    <Card title={t('progress.sessions.hourly.title')} loading={query.isLoading}>
      {buckets.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Flex vertical gap={token.margin}>
          <Timeline
            items={buckets.map((bucket) => ({
              key: bucket.bucket,
              children: (
                <Flex vertical gap={token.marginXXS}>
                  <Typography.Text type="secondary">
                    {formatDateTime(bucket.bucket)}
                  </Typography.Text>
                  {/* delta_ratio is a 0..1 fraction of the product. */}
                  <Typography.Text strong>
                    {`${formatSecondsVerbose(bucket.delta_seconds)} (${bucket.delta_ratio.toFixed(1)}%)`}
                  </Typography.Text>
                </Flex>
              ),
            }))}
          />
          {(result?.total ?? 0) > limit && (
            <Button
              block
              loading={query.isFetching}
              onClick={() => setLimit((prev) => prev + PAGE)}
            >
              {t('progress.sessions.hourly.load_more')}
            </Button>
          )}
        </Flex>
      )}
    </Card>
  );
};
